import { useState, useEffect } from "react";
import PropTypes from "prop-types"; // Add PropTypes import
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/Send";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import ErrorIcon from "@mui/icons-material/Error";
import { useAuth } from "contexts/AuthContext";
import { paymentService } from "services/firestoreService";

// Mock data for the distributions
const distributions = [
  {
    id: "DIST-2023Q4",
    property: "Sunset Apartments",
    owner: "James Wilson",
    amount: 12450.75,
    date: "2023-12-15",
    status: "completed",
    method: "Direct Deposit",
  },
  {
    id: "DIST-2024Q1",
    property: "Sunset Apartments",
    owner: "James Wilson",
    amount: 13875.50,
    date: "2024-03-15",
    status: "completed",
    method: "Direct Deposit",
  },
  {
    id: "DIST-2024Q2",
    property: "Sunset Apartments",
    owner: "James Wilson",
    amount: 14210.25,
    date: "2024-06-15",
    status: "pending",
    method: "Direct Deposit",
  },
  {
    id: "DIST-2023Q4B",
    property: "Highland Towers",
    owner: "Sarah Johnson",
    amount: 8750.00,
    date: "2023-12-15",
    status: "completed",
    method: "Check",
  },
  {
    id: "DIST-2024Q1B",
    property: "Highland Towers",
    owner: "Sarah Johnson",
    amount: 9125.30,
    date: "2024-03-15",
    status: "completed",
    method: "Check",
  },
  {
    id: "DIST-2024Q2B",
    property: "Highland Towers",
    owner: "Sarah Johnson",
    amount: 9550.80,
    date: "2024-06-15",
    status: "failed",
    method: "Check",
  },
  {
    id: "DIST-2023Q4C",
    property: "Parkview Residences",
    owner: "Robert Chen",
    amount: 22150.45,
    date: "2023-12-15",
    status: "completed",
    method: "Wire Transfer",
  },
  {
    id: "DIST-2024Q1C",
    property: "Parkview Residences",
    owner: "Robert Chen",
    amount: 23510.60,
    date: "2024-03-15",
    status: "completed",
    method: "Wire Transfer",
  },
  {
    id: "DIST-2024Q2C",
    property: "Parkview Residences",
    owner: "Robert Chen",
    amount: 24875.25,
    date: "2024-06-15",
    status: "pending",
    method: "Wire Transfer",
  },
];

// Mock data for the chart
const chartData = [
  { month: "Jan", amount: 42300 },
  { month: "Feb", amount: 44100 },
  { month: "Mar", amount: 46500 },
  { month: "Apr", amount: 45200 },
  { month: "May", amount: 47800 },
  { month: "Jun", amount: 48600 },
];

// Status component with appropriate icon and color
const StatusBadge = ({ status }) => {
  if (status === "completed") {
    return (
      <Chip
        icon={<CheckCircleIcon />}
        label="Completed"
        size="small"
        sx={{ backgroundColor: "#66BB6A", color: "white" }}
      />
    );
  } else if (status === "pending") {
    return (
      <Chip
        icon={<PendingIcon />}
        label="Pending"
        size="small"
        sx={{ backgroundColor: "#FFA726", color: "white" }}
      />
    );
  } else {
    return (
      <Chip
        icon={<ErrorIcon />}
        label="Failed"
        size="small"
        sx={{ backgroundColor: "#EF5350", color: "white" }}
      />
    );
  }
};

// Add PropTypes validation
StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
};

function OwnerDistributions() {
  const { user } = useAuth();
  const [distributions, setDistributions] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchDistributions = async () => {
      try {
        const data = await paymentService.getAll(user.uid);
        const dists = data.filter(p => p.type === 'distribution');
        setDistributions(dists);
      } catch (err) {
        console.error("Failed to fetch distributions:", err);
      } finally {
        setLoadingData(false);
      }
    };
    fetchDistributions();
  }, [user]);
  const [tabValue, setTabValue] = useState(0);
  const [period, setPeriod] = useState("Q2 2024");
  const [filterProperty, setFilterProperty] = useState("");

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handlePeriodChange = (event) => {
    setPeriod(event.target.value);
  };

  const handlePropertyChange = (event) => {
    setFilterProperty(event.target.value);
  };

  // Filter distributions based on the selected property (if any)
  const filteredDistributions = filterProperty
    ? distributions.filter((dist) => dist.property === filterProperty)
    : distributions;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Grid container spacing={3}>
          {/* Header and summary card */}
          <Grid item xs={12}>
            <Card sx={{ padding: 3, mb: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="h4" fontWeight="medium">
                    Owner Distributions
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="text" mt={1}>
                    View and manage distributions sent to property owners.
                  </ArgonTypography>
                </Grid>
                <Grid item xs={12} md={6} sx={{ textAlign: "right" }}>
                  <ArgonButton
                    variant="contained"
                    color="primary"
                    startIcon={<SendIcon />}
                  >
                    Create New Distribution
                  </ArgonButton>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* Distribution Summary */}
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ height: "100%" }}>
              <ArgonBox p={2}>
                <ArgonTypography variant="subtitle2" color="text">
                  Total Distributed (2024)
                </ArgonTypography>
                <ArgonTypography variant="h3" fontWeight="bold" mt={1}>
                  $274,500.45
                </ArgonTypography>
                <ArgonTypography variant="body2" color="success" mt={1}>
                  +12.4% from last year
                </ArgonTypography>
              </ArgonBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ height: "100%" }}>
              <ArgonBox p={2}>
                <ArgonTypography variant="subtitle2" color="text">
                  Upcoming Distributions
                </ArgonTypography>
                <ArgonTypography variant="h3" fontWeight="bold" mt={1}>
                  $48,636.30
                </ArgonTypography>
                <ArgonTypography variant="body2" color="text" mt={1}>
                  Due in 14 days
                </ArgonTypography>
              </ArgonBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ height: "100%" }}>
              <ArgonBox p={2}>
                <ArgonTypography variant="subtitle2" color="text">
                  Active Owners
                </ArgonTypography>
                <ArgonTypography variant="h3" fontWeight="bold" mt={1}>
                  24
                </ArgonTypography>
                <ArgonTypography variant="body2" color="success" mt={1}>
                  +3 since last quarter
                </ArgonTypography>
              </ArgonBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ height: "100%" }}>
              <ArgonBox p={2}>
                <ArgonTypography variant="subtitle2" color="text">
                  Processing Issues
                </ArgonTypography>
                <ArgonTypography variant="h3" fontWeight="bold" mt={1}>
                  1
                </ArgonTypography>
                <ArgonTypography variant="body2" color="error" mt={1}>
                  Requires attention
                </ArgonTypography>
              </ArgonBox>
            </Card>
          </Grid>

          {/* Distribution Chart - Simple Version without recharts */}
          <Grid item xs={12}>
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="h5" fontWeight="medium">
                  Distribution Trends
                </ArgonTypography>
                <ArgonTypography variant="body2" color="text" mb={2}>
                  Monthly distributions across all properties
                </ArgonTypography>
                <Box sx={{ height: 300, display: "flex", alignItems: "flex-end", justifyContent: "space-between", px: 4 }}>
                  {chartData.map((item, index) => (
                    <Box key={index} sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "16%" }}>
                      <Box 
                        sx={{ 
                          width: "100%", 
                          height: `${(item.amount / 50000) * 100}%`, 
                          backgroundColor: "#3f51b5", 
                          borderRadius: "4px 4px 0 0",
                          minHeight: 20,
                        }} 
                      />
                      <ArgonTypography variant="body2" mt={1}>
                        {item.month}
                      </ArgonTypography>
                      <ArgonTypography variant="caption" fontWeight="bold">
                        ${item.amount.toLocaleString()}
                      </ArgonTypography>
                    </Box>
                  ))}
                </Box>
              </ArgonBox>
            </Card>
          </Grid>

          {/* Distribution List */}
          <Grid item xs={12}>
            <Card>
              <ArgonBox p={3}>
                <Grid container alignItems="center" spacing={2} mb={3}>
                  <Grid item xs={12} md={7}>
                    <Tabs
                      value={tabValue}
                      onChange={handleTabChange}
                      textColor="primary"
                      indicatorColor="primary"
                    >
                      <Tab label="All Distributions" />
                      <Tab label="Pending" />
                      <Tab label="Completed" />
                      <Tab label="Failed" />
                    </Tabs>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={5}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Period</InputLabel>
                          <Select
                            value={period}
                            label="Period"
                            onChange={handlePeriodChange}
                          >
                            <MenuItem value="Q2 2024">Q2 2024</MenuItem>
                            <MenuItem value="Q1 2024">Q1 2024</MenuItem>
                            <MenuItem value="Q4 2023">Q4 2023</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} md={5}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Property</InputLabel>
                          <Select
                            value={filterProperty}
                            label="Property"
                            onChange={handlePropertyChange}
                          >
                            <MenuItem value="">All Properties</MenuItem>
                            <MenuItem value="Sunset Apartments">
                              Sunset Apartments
                            </MenuItem>
                            <MenuItem value="Highland Towers">
                              Highland Towers
                            </MenuItem>
                            <MenuItem value="Parkview Residences">
                              Parkview Residences
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <IconButton color="primary">
                          <DownloadIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <TableContainer>
                  <Table sx={{ minWidth: 650 }} aria-label="distribution table">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Property</TableCell>
                        <TableCell>Owner</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredDistributions.map((row) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {row.id}
                          </TableCell>
                          <TableCell>{row.property}</TableCell>
                          <TableCell>{row.owner}</TableCell>
                          <TableCell>
                            ${row.amount.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </TableCell>
                          <TableCell>{row.date}</TableCell>
                          <TableCell>{row.method}</TableCell>
                          <TableCell>
                            <StatusBadge status={row.status} />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small">
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </ArgonBox>
            </Card>
          </Grid>
        </Grid>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default OwnerDistributions;